// TradingRulesPage — All trading rules from Section 13 of the Terms
// 13.1-13.14 prohibited practices, 13.13 max 30% margin, news trading 2-min rule, etc.

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TfCard, TfCardFloating, TfSectionHeaderView, TfStatRow } from '../../components/TfPrimitives';

const RULES = [
  { id: 'r1',  category: 'Prohibited', title: 'Fraudulent activities',              desc: 'No market manipulation, spoofing, or artificial market conditions.', severity: 'critical' },
  { id: 'r2',  category: 'Prohibited', title: 'Exploiting system errors',            desc: 'No latency arbitrage, exploiting data feed delays, or system bugs.', severity: 'critical' },
  { id: 'r3',  category: 'Prohibited', title: 'Coordinated trading',                desc: 'No coordinated trades between linked accounts or other clients (incl. opposing positions, shared VPS, group trading).', severity: 'critical' },
  { id: 'r4',  category: 'Prohibited', title: 'Excessive risk patterns',             desc: 'No account rolling, overleveraging, or trades contradicting market standards.', severity: 'high' },
  { id: 'r5',  category: 'Prohibited', title: 'Artificial lot manipulation',         desc: 'No manipulating lot sizes or small trades to meet trading day requirements.', severity: 'high' },
  { id: 'r6',  category: 'Prohibited', title: 'Arbitrage trading',                   desc: 'No latency, hedge, reverse, or rollover scalping arbitrage.', severity: 'critical' },
  { id: 'r7',  category: 'Prohibited', title: 'High-frequency trading',              desc: 'No HFT strategies, trading within 2 min of high-impact news, or gap trading.', severity: 'high' },
  { id: 'r8',  category: 'Prohibited', title: 'Third-party tick scalping EAs',       desc: 'No external EAs for tick scalping. Must own source code of any EA used.', severity: 'high' },
  { id: 'r9',  category: 'Prohibited', title: 'Excessive trade volume',              desc: 'No abnormally high trade count or large lots with minimal pip movement.', severity: 'medium' },
  { id: 'r10', category: 'Prohibited', title: 'Over 30% margin per trade',           desc: 'No single trade idea using more than 30% of available margin. No gambling behavior.', severity: 'critical' },
  { id: 'r11', category: 'Prohibited', title: 'Account rolling',                     desc: 'No repeatedly failing and restarting accounts to abuse risk-free trading.', severity: 'high' },
  { id: 'r12', category: 'Prohibited', title: 'Off-market price exploitation',       desc: 'Trades on demonstrably off-market prices will be cancelled. P&L annulled.', severity: 'critical' },
  { id: 'r13', category: 'Allowed',    title: 'Copy trading across accounts',        desc: 'You may copy trades between your own challenge and funded accounts.', severity: 'info' },
  { id: 'r14', category: 'Allowed',    title: 'Multiple trading platforms',         desc: 'Use any supported platform: Deepchart, Rithmic, NinjaTrader, etc.', severity: 'info' },
  { id: 'r15', category: 'Allowed',    title: 'Manual + AI bot trading',             desc: 'Combine manual discretion with AI bot execution.', severity: 'info' },
  { id: 'r16', category: 'Allowed',    title: 'Holding positions to EOD',           desc: 'Positions held past 15:55 CT get auto-flattened at 16:00 CT (Daily Pause).', severity: 'info' },
  { id: 'r17', category: 'Prohibited', title: 'Off-platform copy-trading services',  desc: 'No third-party services mirroring trades to accounts other than your own.', severity: 'high' },
  { id: 'r18', category: 'Prohibited', title: 'VPN / proxy to bypass geography',     desc: 'No VPNs, proxies, or tools to misrepresent your country of residence.', severity: 'critical' },
  { id: 'r19', category: 'Prohibited', title: 'Account sharing',                     desc: 'Login credentials are personal. No sharing with employees, family, or other traders.', severity: 'critical' },
  { id: 'r20', category: 'Prohibited', title: 'Disqualified demo behavior',          desc: 'Trading indicating high likelihood of significant losses may be flagged during eval.', severity: 'high' },
];

const SEV_META: Record<string, { color: string; bg: string; label: string; icon: string }> = {
  critical: { color: '#FF3B30', bg: 'rgba(255,59,48,0.08)', label: 'Account breach', icon: '⛔' },
  high:     { color: '#FF9500', bg: 'rgba(255,149,0,0.08)', label: 'Warning + review', icon: '⚠️' },
  medium:   { color: '#FF9500', bg: 'rgba(255,149,0,0.05)', label: 'Review', icon: '⚠️' },
  info:     { color: '#34C759', bg: 'rgba(52,199,89,0.08)',  label: 'Allowed',        icon: '✓'  },
};

export function TradingRulesPage() {
  const nav = useNavigate();
  const [filter, setFilter] = useState<'all' | 'Prohibited' | 'Allowed' | 'critical' | 'high'>('all');
  const [expanded, setExpanded] = useState<string | null>(null);

  const filtered = RULES.filter((r) => {
    if (filter === 'all') return true;
    if (filter === 'Prohibited' || filter === 'Allowed') return r.category === filter;
    return r.severity === filter;
  });

  const counts = {
    prohibited: RULES.filter((r) => r.category === 'Prohibited').length,
    allowed: RULES.filter((r) => r.category === 'Allowed').length,
    critical: RULES.filter((r) => r.severity === 'critical').length,
  };

  return (
    <div className="min-h-screen bg-tf-surface-1 pb-8">
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-tf-border ios26-navbar px-2 backdrop-blur-xl">
        <button onClick={() => nav(-1)} data-testid="back" className="flex h-9 w-9 items-center justify-center text-tf-blue haptic">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold">Trading rules</div>
        <div className="w-9" />
      </div>

      {/* Hero — white floating card with rule count + pill buttons */}
      <div className="mx-4 mt-4">
        <TfCardFloating className="text-center">
          <div className="text-[13px]" style={{ color: '#8E8E93' }}>Section 13 · Trading rules</div>
          <div className="mt-1 flex items-baseline justify-center gap-1">
            <span className="text-[40px] font-bold tabular-nums leading-none" style={{ color: '#1C1C1E' }}>{RULES.length}</span>
            <span className="text-[15px] text-tf-text-3">rules</span>
          </div>
          <div className="mt-1 flex items-center justify-center gap-1 text-[13px]">
            <span className="text-tf-text-3">Stay compliant · trade with confidence</span>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <button onClick={() => setFilter('critical')} className="flex h-12 items-center justify-center gap-1.5 rounded-full bg-tf-text-1 text-[15px] font-semibold text-white active:scale-[0.97] transition-transform">
              Critical
              <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M7 17L17 7" /><path d="M7 7h10v10" />
              </svg>
            </button>
            <button onClick={() => setFilter('Allowed')} className="flex h-12 items-center justify-center gap-1.5 rounded-full bg-tf-text-1 text-[15px] font-semibold text-white active:scale-[0.97] transition-transform">
              Allowed
              <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 7L7 17" /><path d="M17 17H7V7" />
              </svg>
            </button>
          </div>
        </TfCardFloating>
      </div>

      {/* 3-up stat row */}
      <TfStatRow
        className="mt-4"
        stats={[
          { label: 'Critical', value: `${counts.critical}`, color: '#FF3B30' },
          { label: 'Prohibited', value: `${counts.prohibited}`, color: '#FF9500' },
          { label: 'Allowed', value: `${counts.allowed}`, color: '#34C759' },
        ]}
      />

      {/* Filters */}
      <div className="px-4 pt-4 flex items-center gap-1.5 overflow-x-auto">
        {(['all', 'Prohibited', 'Allowed', 'critical', 'high'] as const).map((f) => (
          <button
            key={f} onClick={() => setFilter(f)}
            data-testid={`rule-filter-${f}`}
            className={['shrink-0 rounded-full px-3 py-1.5 text-[12px] font-semibold capitalize transition',
              filter === f ? 'bg-tf-text-1 text-white' : 'bg-white border border-tf-border text-tf-text-1 active:opacity-40'
            ].join(' ')}
          >
            {f}
          </button>
        ))}
      </div>

      {/* Rules list */}
      <TfSectionHeaderView title={`Rules · ${filtered.length}`} />
      <div className="mx-4 space-y-2">
        {filtered.map((r) => {
          const s = SEV_META[r.severity];
          const open = expanded === r.id;
          return (
            <div key={r.id} data-testid={`rule-${r.id}`} className="rounded-[16px] bg-white overflow-hidden" style={{ boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
              <button
                onClick={() => setExpanded(open ? null : r.id)}
                className="flex w-full items-center gap-3 px-4 py-3 text-left active:bg-tf-surface-1"
              >
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] text-[18px]" style={{ backgroundColor: s.bg, color: s.color }}>{s.icon}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[14px] font-bold text-tf-text-1">{r.title}</span>
                  </div>
                  <div className="text-[11px] text-tf-text-3">{r.category} · {s.label}</div>
                </div>
                <svg viewBox="0 0 24 24" className={['h-4 w-4 text-tf-text-muted transition-transform', open ? 'rotate-90' : ''].join(' ')} fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </button>
              {open && (
                <div className="px-4 pb-3 pt-1 border-t border-tf-border">
                  <p className="text-[13px] text-tf-text-1 leading-relaxed">{r.desc}</p>
                  {r.severity === 'critical' && (
                    <div className="mt-2 rounded-[8px] bg-tf-red-tint border border-tf-red/30 p-2 text-[11px] text-tf-loss">
                      <span className="font-bold">⛔ Immediate breach</span> — Violation will result in account termination and forfeit of any pending payouts.
                    </div>
                  )}
                  {r.severity === 'high' && (
                    <div className="mt-2 rounded-[8px] bg-tf-orange-tint border border-tf-orange/30 p-2 text-[11px] text-tf-warning">
                      <span className="font-bold">⚠️ Review required</span> — May trigger a compliance review and account suspension.
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <p className="mt-3 px-4 text-center text-[10px] text-tf-text-3">Full rules in Section 13 of Terms & Conditions. We monitor all activity.</p>
    </div>
  );
}
